import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  TrendingUp,
  Wallet,
  ArrowUpRight,
  ShieldCheck,
  ChevronRight,
  Zap,
} from "lucide-react";
import {
  getRecommendedTransferPartners,
  getValuationAnalytics,
} from "../../../services/Front/dashboardService";
import { decryptData } from "../../../utils/crypto";
import Utils from "../../../utils";
import { notify } from "../../../components/Common/Alerts";

const DashboardScreen = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [cardList, setCardList] = useState("");

  useEffect(() => {
    getValuationAnalytics()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        setCardList(decrypted);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  useEffect(() => {
    if (cardList?.cards?.length > 0) {
      getPartners();
    }
  }, [cardList?.cards]);

  const getPartners = async () => {
    try {
      const response = await getRecommendedTransferPartners({
        card_ids: cardList?.cards?.map((c) => c.card_id),
      });
      const decrypted = decryptData(response.data.data);
      setPartners(decrypted || []);
    } catch (error) {
      notify.error(Utils.getErrorMessage(error));
    }
  };

  const strongestProgramData = (cardList?.cards || []).reduce((acc, card) => {
    const program = card.reward_program_name || "Unknown";
    const estimatedValue = Number(card.latest_max || 0);
    acc[program] = (acc[program] || 0) + estimatedValue;
    return acc;
  }, {});

  const strongestProgram = Object.entries(strongestProgramData || {}).sort(
    (a, b) => b[1] - a[1]
  )[0];

  return (
    <div className="bg-[#F8FAFC] dark:bg-[#070C19] text-slate-800 dark:text-slate-100 font-sans antialiased transition-colors duration-300">
      
      <main className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-6 lg:py-10">
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 xl:gap-8 items-start">
          
          <div className="lg:col-span-8 space-y-6">
            
            <div className="relative rounded-3xl bg-gradient-to-br from-[#CAA036] via-[#B8860B] to-[#8B6508] p-6 sm:p-8 md:p-10 text-white overflow-hidden shadow-lg border border-amber-600/10">
              <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-10 pointer-events-none hidden md:block">
                <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <path d="M0,100 C30,40 70,60 100,0 L100,100 Z" fill="white" />
                </svg>
              </div>
              
              <div className="relative z-10 space-y-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md">
                  <Zap size={14} className="text-amber-200 fill-amber-200" />
                  <span className="text-xs font-semibold tracking-wide text-amber-100">PREMIUM ACCESS</span>
                </div>
                
                <div className="space-y-3">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold tracking-normal leading-tight">
                    Know the real value <br className="hidden sm:inline" />of your points.
                  </h1>
                  <p className="text-sm sm:text-base font-normal text-amber-50/80 max-w-xl leading-relaxed">
                    Real-time market insights & smart redemption recommendations for maximum portfolio utility.
                  </p>
                </div>

                <div className="pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-white/10">
                  <div className="flex flex-wrap gap-8 md:gap-12">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-xl bg-white/10 border border-white/10 shadow-inner">
                        <Wallet size={18} />
                      </div>
                      <div>
                        <p className="text-lg sm:text-xl xl:text-2xl font-bold tabular-nums">{(cardList.total_points || 0).toLocaleString()}</p>
                        <p className="text-xs text-amber-100/70 font-medium tracking-normal mt-0.5">Total Points</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-xl bg-white/10 border border-white/10 shadow-inner">
                        <TrendingUp size={18} />
                      </div>
                      <div>
                        <p className="text-lg sm:text-xl xl:text-2xl font-bold tabular-nums whitespace-nowrap">
                          ${cardList.total_estimated_min_value || 0} – ${cardList.total_estimated_max_value || 0}
                        </p>
                        <p className="text-xs text-amber-100/70 font-medium tracking-normal mt-0.5">Estimated Value</p>
                      </div>
                    </div>
                  </div>

                  <button 
                    onClick={() => navigate("/search")}
                    className="w-full sm:w-auto px-6 h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-white text-slate-900 text-sm font-semibold hover:bg-slate-50 active:scale-[0.98] transition-all shadow-md whitespace-nowrap"
                  >
                    Find Redemptions
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:hidden">
              <div className="bg-white dark:bg-[#0B1222] border border-slate-200/60 dark:border-slate-800 p-5 rounded-2xl shadow-sm flex items-start gap-4">
                <div className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 text-emerald-500 mt-0.5">
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Portfolio Status</p>
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 mt-1">Ready for Redemption</h3>
                </div>
              </div>

              <div className="bg-white dark:bg-[#0B1222] border border-slate-200/60 dark:border-slate-800 p-5 rounded-2xl shadow-sm flex items-start gap-4">
                <div className="p-2 rounded-xl bg-blue-50 dark:bg-blue-950/30 text-blue-500 mt-0.5">
                  <Wallet size={20} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Points</p>
                  <h3 className="text-base font-bold text-slate-900 dark:text-white mt-1">
                    {cardList.total_points?.toLocaleString() || 0}
                  </h3>
                </div>
              </div>

              <div className="bg-white dark:bg-[#0B1222] border border-slate-200/60 dark:border-slate-800 p-5 rounded-2xl shadow-sm flex items-start gap-4">
                <div className="p-2 rounded-xl bg-cyan-50 dark:bg-cyan-950/30 text-cyan-500 mt-0.5">
                  <TrendingUp size={20} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Est. Market Value</p>
                  <h3 className="text-base font-bold text-slate-900 dark:text-white mt-1">
                    ${cardList.total_estimated_min_value} – ${cardList.total_estimated_max_value}
                  </h3>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-[#0B1222] border border-slate-200/60 dark:border-slate-800/60 p-6 sm:p-8 rounded-2xl shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2.5">
                  <TrendingUp size={18} className="text-[#CAA036]" />
                  <h2 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-normal">Recommended Transfer Partners</h2>
                </div>
                <button className="text-xs font-semibold text-[#B8860B] hover:text-[#CAA036] transition-colors hover:underline">View all</button>
              </div>

              {partners?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {partners.map((partner) => (
                    <div key={partner.id} className="p-5 rounded-xl bg-slate-50/50 dark:bg-[#0F172A]/50 border border-slate-200/50 dark:border-slate-800/50 flex flex-col justify-between gap-5 hover:border-amber-500/30 transition-all duration-200 group">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-white dark:bg-[#0B1222] border border-slate-200 dark:border-slate-800 flex items-center justify-center font-bold text-[#CAA036] group-hover:scale-105 transition-transform duration-200 shadow-sm">
                            <ArrowUpRight size={18} />
                          </div>
                          <div>
                            <h4 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">{partner.partner_name}</h4>
                            <p className="text-xs text-slate-400 mt-1">From {partner.source_program}</p>
                          </div>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-1 rounded-md tracking-wider ${
                          Number(partner.avg_cpp) >= 2 
                            ? "bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400" 
                            : "bg-amber-50 dark:bg-amber-950/20 text-amber-600 dark:text-amber-400"
                        }`}>
                          {Number(partner.avg_cpp) >= 2 ? "EXCELLENT" : "GOOD"}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-200/60 dark:border-slate-800/60">
                        <div>
                          <p className="text-xs font-medium text-slate-400">Transfer Ratio</p>
                          <p className="text-sm font-bold text-slate-800 dark:text-white mt-0.5">{Number(partner.transfer_ratio)}:1</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-medium text-slate-400">Avg CPP</p>
                          <p className="text-sm font-bold text-[#B8860B] dark:text-[#CAA036] mt-0.5">{Number(partner.avg_cpp)}</p>
                        </div>
                      </div>

                      {Number(partner.bonus_percentage) > 0 && (
                        <div className="inline-flex self-start rounded-md bg-amber-500/10 border border-amber-500/20 px-2.5 py-1">
                          <p className="text-xs text-amber-700 dark:text-amber-400 font-semibold">
                            +{partner.bonus_percentage}% Transfer Bonus
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-slate-50/50 dark:bg-[#0F172A]/50 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
                  <p className="text-sm text-slate-400 font-medium">No conversion routes active for your portfolio assets.</p>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-4 space-y-6">
            
            <div className="hidden lg:grid grid-cols-1 gap-4">
              <div className="bg-white dark:bg-[#0B1222] border border-slate-200/60 dark:border-slate-800/60 p-5 rounded-2xl shadow-sm flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 text-emerald-500 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Portfolio Status</p>
                  <h3 className="text-sm font-bold text-slate-800 dark:text-white mt-0.5">Ready for Redemption</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Optimized for high value redemptions.</p>
                </div>
              </div>

              <div className="bg-white dark:bg-[#0B1222] border border-slate-200/60 dark:border-slate-800/60 p-5 rounded-2xl shadow-sm flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-cyan-50 dark:bg-cyan-950/30 text-cyan-500 flex items-center justify-center flex-shrink-0">
                  <TrendingUp size={20} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Estimated Aggregate Valuation</p>
                  <h3 className="text-base font-bold text-slate-800 dark:text-white mt-0.5 tabular-nums">
                    ${cardList.total_estimated_min_value} – ${cardList.total_estimated_max_value}
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">Based on current market valuations.</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-[#0B1222] border border-slate-200/60 dark:border-slate-800/60 p-6 rounded-2xl shadow-sm space-y-5">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-100 dark:border-slate-800/80">
                <Sparkles size={16} className="text-[#CAA036]" />
                <h2 className="text-sm font-bold uppercase tracking-wide text-slate-900 dark:text-white">YOUR ASSET ACCOUNTS</h2>
              </div>

              <div className="divide-y divide-slate-100 dark:divide-slate-800/50">
                {cardList?.cards?.map((card, index) => (
                  <div key={index} className="py-4 flex items-center justify-between gap-3 first:pt-0 last:pb-0 group cursor-pointer">
                    <div className="flex items-center gap-3.5 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center flex-shrink-0">
                        <Wallet size={16} />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white truncate group-hover:text-[#CAA036] transition-colors">
                          {card.name}
                        </h4>
                        <p className="text-xs text-slate-400 truncate mt-0.5">
                          {card.reward_program_name}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-900 dark:text-white tabular-nums">
                          {Number(card.latest_points || 0).toLocaleString()}
                        </p>
                        <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mt-0.5">
                          Points
                        </p>
                      </div>
                      <ChevronRight size={14} className="text-slate-300 dark:text-slate-600 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                ))}
              </div>

              <button 
                onClick={() => navigate("/profile-point-wallet")}
                className="w-full h-11 rounded-xl bg-[#CAA036]/10 hover:bg-[#CAA036]/20 text-[#9A721C] dark:text-amber-400 text-sm font-semibold transition-all flex items-center justify-center gap-2 border border-amber-500/10"
              >
                View All Accounts
                <ArrowRight size={14} />
              </button>
            </div>

            <div className="bg-emerald-500/5 dark:bg-emerald-950/20 border border-emerald-500/10 dark:border-emerald-500/20 p-5 rounded-2xl flex flex-col justify-between gap-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-emerald-500/10">
                  <Sparkles size={18} />
                </div>
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">STRONGEST PROGRAM LINK</span>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white mt-0.5 truncate max-w-[200px]">
                    {strongestProgram?.[0] || "N/A"}
                  </h3>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-200/40 dark:border-slate-800/40 flex justify-between items-center">
                <span className="text-xs font-medium text-slate-400">Yield Range</span>
                <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 tracking-normal tabular-nums">
                  ${cardList.total_estimated_min_value || 0} – ${cardList.total_estimated_max_value || 0}
                </p>
              </div>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardScreen;